// Rumbo efectivo de una unidad.
//
// El equipo GPS manda `course` en cada posición, pero medido contra
// producción solo el 15% de las unidades en movimiento reporta un valor real:
// el 85% envía 0. Presentar ese 0 como "Norte" afirma una dirección que nadie
// midió — se vio una unidad viajando al sur mientras la ficha decía "0° N".
//
// Cuando el equipo no lo reporta, el rumbo se deriva del desplazamiento real
// entre posiciones consecutivas, que sí lo sabe.

// Metros entre dos coordenadas [lng, lat].
export function distanciaMetros(a, b) {
  const R = 6371000
  const dLat = (b[1] - a[1]) * Math.PI / 180
  const dLon = (b[0] - a[0]) * Math.PI / 180
  const lat1 = a[1] * Math.PI / 180
  const lat2 = b[1] * Math.PI / 180
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

// Rumbo en grados (0-360, 0 = norte) del tramo a → b.
export function bearingEntre(a, b) {
  const lat1 = a[1] * Math.PI / 180
  const lat2 = b[1] * Math.PI / 180
  const dLon = (b[0] - a[0]) * Math.PI / 180
  const y = Math.sin(dLon) * Math.cos(lat2)
  const x = Math.cos(lat1) * Math.sin(lat2)
    - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

// Tramos por debajo de esta distancia dan un ángulo dominado por el ruido del
// receptor, no por el movimiento.
export const MIN_TRAMO_METROS = 15

// Cuántos tramos se miran hacia atrás buscando uno con recorrido real.
const MAX_TRAMOS_ATRAS = 6

// Rumbo derivado de una serie de posiciones [[lng,lat], ...] en orden
// cronológico. Devuelve grados o null si ningún tramo reciente tiene
// desplazamiento suficiente.
//
// Se recorre hacia atrás y no se mira solo el último par: la serie suele
// rematar con la posición actual repetida, así que ese tramo mide 0 m y el
// rumbo se perdería casi siempre.
export function rumboDesdeRecorrido(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return null
  const tope = Math.max(1, coords.length - MAX_TRAMOS_ATRAS)
  for (let k = coords.length - 1; k >= tope; k--) {
    const a = coords[k - 1]
    const b = coords[k]
    if (!a || !b || a.length < 2 || b.length < 2) continue
    if (distanciaMetros(a, b) > MIN_TRAMO_METROS) return bearingEntre(a, b)
  }
  return null
}

// Rumbo a mostrar, en grados, o null si no hay uno confiable.
//
// - `course` del equipo manda cuando trae un valor real (distinto de 0).
// - Un 0 se trata como "no reportado", no como norte: es el caso del 85%.
// - Sin course se deriva del recorrido.
// - Detenida no se devuelve rumbo: conserva el último valor recibido y
//   mostrarlo sugiere una dirección que ya no existe.
export function resolverRumbo(course, coords, velocidadKmh) {
  if (velocidadKmh != null && velocidadKmh <= 2) return null
  const g = Number(course)
  if (!isNaN(g) && g !== 0) return g
  return rumboDesdeRecorrido(coords)
}
